// Ported from microsites/womens-conference/src/components/Lunch.tsx; next/image -> img (paths arrive resolved, lib/views.ts).
import { useState } from 'react';
import { FoodMark } from './Botanical';
import type { Caterer, LunchOption } from '../lib/conference';

/**
 * The lunch box, pictured.
 *
 * Every thumb is the same square whether or not a photograph exists, so a
 * missing file or an option the caterer has no picture for (the allergy path)
 * changes what is inside the box and never the shape of the row. `onError` is
 * doing real work here: the photos are dropped into public/art/lunch/ by hand,
 * so the fallback has to be a runtime one, not a build-time assumption.
 */
export function LunchThumb({
  option,
  size = 64,
  selected = false,
}: {
  option: Pick<LunchOption, 'photo' | 'mark'>;
  size?: number;
  selected?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const shell = `relative shrink-0 overflow-hidden rounded-xl ${
    selected ? 'ring-wm-sage-800/25 ring-1' : ''
  }`;

  if (option.photo && !failed) {
    return (
      <span className={`${shell} bg-wm-sage-100`} style={{ width: size, height: size }}>
        <img
          src={option.photo}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }

  return (
    <span
      className={`${shell} bg-wm-cream flex items-center justify-center`}
      style={{ width: size, height: size }}
    >
      {option.mark ? (
        <FoodMark
          kind={option.mark}
          className={selected ? 'text-wm-sage-700' : 'text-wm-sage-300'}
        />
      ) : null}
    </span>
  );
}

/**
 * Who made the food, said plainly under the choices.
 *
 * Deliberately quiet and deliberately not adjacent to our own mark: it tells
 * an attendee where Saturday's box comes from, which is a fact about the food.
 * The wordmark falls back to the name in type if the file is not there.
 */
export function CatererCredit({
  caterer,
  className = '',
}: {
  caterer: Caterer | undefined;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (!caterer) return null;

  return (
    <p className={`text-wm-sage-500 flex items-center gap-1.5 text-xs ${className}`}>
      <span>Catered by</span>
      {caterer.logo && !failed ? (
        <img
          src={caterer.logo}
          alt={caterer.name}
          className="h-4 w-auto"
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="font-medium">{caterer.name}</span>
      )}
    </p>
  );
}
